import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { NavigationProvider } from './context/NavigationContext';
import { Navbar, ProtectedRoute } from './components/common';
import Home from './pages/public/Home';
import Login from './pages/public/Login';
import { TournamentsList, TournamentDetail } from './pages/public/Tournaments';
import { Matches as LiveScores } from './pages/public/LiveScores';
import { PlayersList, PlayerDetail } from './pages/public/Players';
import MatchDetail from './pages/public/MatchDetail';
import Profile from './pages/public/Profile';
import OrganizerDashboard from './pages/organizer/OrganizerDashboard';
import ManageTournament from './pages/organizer/ManageTournament';
import GroupStageDashboard from './pages/organizer/GroupStageDashboard';
import GroupManager from './pages/organizer/GroupManager';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPlayers from './pages/admin/AdminPlayers';
import AdminTournaments from './pages/admin/AdminTournaments';
import AdminMatches from './pages/admin/AdminMatches';
import NotFound from './pages/public/NotFound';
import './App.css';
import './styles/common.css';
import './styles/organizer.css';

function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    setIsIos(ios);
    const mobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
    setIsMobile(mobile);

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
      setDismissed(true);
    }
  };

  if (!isMobile || isStandalone || dismissed) return null;
  if (!deferredPrompt && !isIos) return null;

  return (
    <div style={installStyles.banner}>
      <div style={installStyles.content}>
        <div style={installStyles.icon}>
          <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
            <defs>
              <radialGradient id="installBall" cx="38%" cy="32%" r="55%"><stop offset="0%" stopColor="#E4FF54" /><stop offset="50%" stopColor="#BDFF00" /><stop offset="100%" stopColor="#7AB800" /></radialGradient>
            </defs>
            <circle cx="32" cy="32" r="22" fill="url(#installBall)" />
            <path d="M18 20c4 6 4 18 0 24" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.7" />
            <path d="M46 20c-4 6-4 18 0 24" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.7" />
          </svg>
        </div>
        <div style={installStyles.text}>
          <div style={installStyles.title}>Install TennisBuzz</div>
          <div style={installStyles.sub}>
            {isIos
              ? 'Tap the share button, then "Add to Home Screen"'
              : 'Add to your home screen for quick access'
            }
          </div>
        </div>
      </div>
      <div style={installStyles.actions}>
        {!isIos && <button onClick={handleInstall} style={installStyles.installBtn}>Install</button>}
        <button onClick={() => setDismissed(true)} style={installStyles.closeBtn}>✕</button>
      </div>
    </div>
  );
}

const installStyles = {
  banner: { position: 'fixed', bottom: 68, left: 8, right: 8, background: '#00205B', borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, zIndex: 1500, boxShadow: '0 8px 32px rgba(0,32,91,0.4)', animation: 'fadeIn 0.3s ease' },
  content: { display: 'flex', alignItems: 'center', gap: 12, flex: 1 },
  icon: { width: 40, height: 40, borderRadius: 10, background: 'rgba(189,255,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  text: { flex: 1 },
  title: { fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5 },
  sub: { fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 },
  actions: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  installBtn: { padding: '8px 18px', background: '#BDFF00', color: '#00205B', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer', fontSize: 12, fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5 },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 16, padding: 4 },
};

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ position: 'fixed', bottom: 74, left: 16, width: 36, height: 36, borderRadius: '50%', background: '#00205B', color: '#BDFF00', border: 'none', cursor: 'pointer', fontSize: 16, boxShadow: '0 4px 12px rgba(0,32,91,0.3)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↑</button>
  );
}

function PushPrompt() {
  const { currentUser: user } = useApp();
  const [show, setShow] = useState(false);

  const subscribe = useCallback(async () => {
    try {
      const { notificationAPI } = await import('./services/api');
      const reg = await navigator.serviceWorker.ready;
      const { data } = await notificationAPI.getVapidKey();
      if (!data.publicKey) return setShow(false);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: data.publicKey,
      });
      await notificationAPI.subscribe(sub.toJSON());
      localStorage.setItem('tennisbuzz_push', '1');
      setShow(false);
    } catch { setShow(false); }
  }, []);

  useEffect(() => {
    if (!user || !('PushManager' in window) || !('serviceWorker' in navigator)) return;
    if (localStorage.getItem('tennisbuzz_push') === '1' || localStorage.getItem('tennisbuzz_push_dismissed') === '1') return;
    // Check if already subscribed
    navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription()).then(sub => {
      if (sub) { localStorage.setItem('tennisbuzz_push', '1'); return; }
      setShow(true);
    }).catch(() => {});
  }, [user]);

  if (!show) return null;

  const dismiss = () => { localStorage.setItem('tennisbuzz_push_dismissed', '1'); setShow(false); };

  return (
    <div style={{ position: 'fixed', bottom: 68, left: 8, right: 8, background: '#00205B', borderRadius: 14, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, zIndex: 1500, boxShadow: '0 8px 32px rgba(0,32,91,0.4)', animation: 'fadeIn 0.3s ease' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>🔔 Enable Notifications</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>Get alerts for live matches & results</div>
      </div>
      <button onClick={subscribe} style={{ padding: '7px 16px', background: '#BDFF00', color: '#00205B', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer', fontSize: 11, fontFamily: "'Outfit', sans-serif" }}>Enable</button>
      <button onClick={dismiss} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 16, padding: 4 }}>✕</button>
    </div>
  );
}

function App() {
  const [dark, setDark] = useState(() => localStorage.getItem('tennisbuzz_theme') === 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('tennisbuzz_theme', dark ? 'dark' : 'light');
  }, [dark]);

  return (
    <AppProvider>
      <BrowserRouter>
        <NavigationProvider>
          <Navbar dark={dark} onToggleTheme={() => setDark(d => !d)} />
          <main style={{ minHeight: 'calc(100vh - 52px)', background: 'var(--bg)' }}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/tournaments" element={<TournamentsList />} />
              <Route path="/tournaments/:id" element={<TournamentDetail />} />
              <Route path="/matches" element={<LiveScores />} />
              <Route path="/live" element={<LiveScores />} />
              <Route path="/players" element={<PlayersList />} />
              <Route path="/players/:id" element={<PlayerDetail />} />
              <Route path="/matches/:id" element={<MatchDetail />} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/organizer" element={<ProtectedRoute role="organizer"><OrganizerDashboard /></ProtectedRoute>} />
              <Route path="/organizer/group-stages" element={<ProtectedRoute role="organizer"><GroupStageDashboard /></ProtectedRoute>} />
              <Route path="/organizer/group-manage/:id" element={<ProtectedRoute role="organizer"><GroupManager /></ProtectedRoute>} />
              <Route path="/organizer/tournament-group-stage/:id" element={<ProtectedRoute role="organizer"><GroupManager /></ProtectedRoute>} />
              <Route path="/organizer/tournament/:id" element={<ProtectedRoute role="organizer"><ManageTournament /></ProtectedRoute>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/players" element={<ProtectedRoute role="admin"><AdminPlayers /></ProtectedRoute>} />
              <Route path="/admin/tournaments" element={<ProtectedRoute role="admin"><AdminTournaments /></ProtectedRoute>} />
              <Route path="/admin/matches" element={<ProtectedRoute role="admin"><AdminMatches /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <InstallPrompt />
          <PushPrompt />
          <BackToTop />
        </NavigationProvider>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
